import IconButton from "@material-ui/core/IconButton"
import CloseIcon from "@material-ui/icons/Close"
import { OptionsObject, SnackbarKey, SnackbarMessage, useSnackbar as useSnackbarNotistack } from "notistack"
import React, { useCallback } from "react"

import useDialogMensagemStore, { SeveridadeMensagem } from "../stores/DialogMensagemStore"

export type { OptionsObject, SnackbarKey, SnackbarMessage, VariantType } from "notistack"
export { SnackbarProvider } from "notistack"

// Erro e aviso exigem que o usuário leia e reconheça, então viram popup central.
// Sucesso e info são confirmações passivas e seguem como faixa (canto superior
// direito), assim como o variant padrão — usado pelas dicas de navegação em
// permissoes.js, que abrem sozinhas ao entrar na tela e não podem virar modal.
const SEVERIDADES_EM_POPUP: SeveridadeMensagem[] = ["error", "warning"]

function ehSeveridadeDePopup(variante?: string): variante is SeveridadeMensagem {
    return SEVERIDADES_EM_POPUP.includes(variante as SeveridadeMensagem)
}

/**
 * Substitui o useSnackbar do notistack em todo o sistema. Mesma API: as telas
 * seguem chamando enqueueSnackbar(mensagem, { variant }) sem saber se aquilo
 * vira popup ou faixa — quem decide é o variant, aqui.
 */
export function useSnackbar() {
    const { enqueueSnackbar: enfileirarFaixa, closeSnackbar } = useSnackbarNotistack()
    const mostrarPopup = useDialogMensagemStore(s => s.mostrar)

    // Botão de fechar de toda faixa: permite dispensar a mensagem na hora e
    // liberar o acesso aos elementos atrás dela, sem esperar o autoHideDuration.
    const acaoFechar = useCallback(
        (key: SnackbarKey) => (
            <IconButton size="small" onClick={() => closeSnackbar(key)}>
                <CloseIcon fontSize="small" htmlColor="#fff" />
            </IconButton>
        ),
        [closeSnackbar]
    )

    const enqueueSnackbar = useCallback(
        (mensagem: SnackbarMessage, opcoes?: OptionsObject): SnackbarKey => {
            const variante = opcoes?.variant
            // Mensagem em ReactNode não caberia no popup (que espera texto); nesse
            // caso mantém a faixa em vez de renderizar "[object Object]".
            if (ehSeveridadeDePopup(variante) && typeof mensagem === "string") {
                mostrarPopup(mensagem, variante)
                return ""
            }
            if (variante === "success") {
                // Sucesso sai no canto INFERIOR direito (em vez do superior, padrão
                // do provider) para não competir com os avisos operacionais do topo
                // (QZ, fatura, etc.).
                return enfileirarFaixa(mensagem, {
                    anchorOrigin: { vertical: "bottom", horizontal: "right" },
                    action: acaoFechar,
                    ...opcoes,
                })
            }
            // Demais faixas (info e variant padrão) também ganham o botão de fechar.
            // O caller ainda pode sobrescrever passando seu próprio `action` em opcoes.
            return enfileirarFaixa(mensagem, { action: acaoFechar, ...opcoes })
        },
        [enfileirarFaixa, mostrarPopup, acaoFechar]
    )

    return { enqueueSnackbar, closeSnackbar }
}
